<template>
  <section class="account-preferences-mobile" :aria-busy="isLoading">
    <h2>{{ $t('views.account_center.preferences') }}</h2>
    <section class="preference-field">
      <h3>{{ $t('views.account_center.language') }}</h3>
      <p>{{ $t('views.account_center.change_language') }}</p>
      <el-skeleton v-if="isLoading" animated :rows="1" />
      <button
        v-else
        class="language-trigger"
        type="button"
        aria-haspopup="dialog"
        :aria-expanded="languageSheetVisible"
        :disabled="!languageOptions.length"
        @click="languageSheetVisible = true"
      >
        <span>{{ currentLanguageName }}</span>
        <el-icon><ArrowRight /></el-icon>
      </button>
    </section>
    <section class="preference-field">
      <h3>{{ $t('views.account_center.appearance') }}</h3>
      <p>{{ $t('views.account_center.theme_customization') }}</p>
      <el-skeleton v-if="isLoading" animated :rows="1" />
      <button v-else class="language-trigger" type="button" aria-haspopup="dialog" :aria-expanded="themeSheetVisible" :disabled="!themeOptions.length" @click="themeSheetVisible = true">
        <span>{{ currentThemeName }}</span>
        <el-icon><ArrowRight /></el-icon>
      </button>
    </section>
    <el-drawer
      v-model="languageSheetVisible"
      class="account-language-sheet"
      direction="btt"
      size="auto"
      :title="$t('views.account_center.language')"
      :show-close="false"
      append-to-body
      @opened="focusSelectedLanguage"
    >
      <div class="language-options" role="group" :aria-label="$t('views.account_center.language')">
        <button
          v-for="item in languageOptions"
          :key="item.id"
          ref="languageButtons"
          type="button"
          class="language-option"
          :class="{ 'is-selected': item.key === languageValue }"
          :aria-pressed="item.key === languageValue"
          @click="selectLanguage(item.key)"
        >
          <span>{{ item.name }}</span>
          <el-icon v-if="item.key === languageValue"><Check /></el-icon>
        </button>
      </div>
      <template #footer>
        <el-button class="language-cancel" @click="languageSheetVisible = false">{{ $t('views.account_center.cancel') }}</el-button>
      </template>
    </el-drawer>
    <el-drawer v-model="themeSheetVisible" class="account-language-sheet" direction="btt" size="auto" :title="$t('views.account_center.appearance')" :show-close="false" append-to-body @opened="focusSelectedTheme">
      <div class="language-options" role="group" :aria-label="$t('views.account_center.appearance')">
        <button v-for="item in themeOptions" :key="item.id" ref="themeButtons" type="button" class="language-option" :class="{ 'is-selected': item.key === themeValue }" :aria-pressed="item.key === themeValue" @click="selectTheme(item.key)">
          <span>{{ item.name }}</span>
          <el-icon v-if="item.key === themeValue"><Check /></el-icon>
        </button>
      </div>
      <template #footer>
        <el-button class="language-cancel" @click="themeSheetVisible = false">{{ $t('views.account_center.cancel') }}</el-button>
      </template>
    </el-drawer>
  </section>
</template>

<script>
import { ArrowRight, Check } from '@element-plus/icons-vue'

export default {
  name: 'MobileAccountPreferences',
  components: { ArrowRight, Check },
  props: {
    isLoading: Boolean,
    themeValue: { type: String, default: '' },
    languageValue: { type: String, default: '' },
    themeOptions: { type: Array, default: () => [] },
    languageOptions: { type: Array, default: () => [] }
  },
  emits: ['update:themeValue', 'update:languageValue', 'theme-change', 'language-change'],
  data() {
    return { languageSheetVisible: false, themeSheetVisible: false }
  },
  computed: {
    currentLanguageName() {
      return this.languageOptions.find(item => item.key === this.languageValue)?.name || this.languageValue
    },
    currentThemeName() {
      return this.themeOptions.find(item => item.key === this.themeValue)?.name || this.themeValue
    }
  },
  methods: {
    selectTheme(key) {
      if (!this.themeSheetVisible || this.isLoading || !this.themeOptions.some(item => item.key === key)) return
      this.themeSheetVisible = false
      if (key === this.themeValue) return
      this.$emit('update:themeValue', key)
      this.$emit('theme-change')
    },
    focusSelectedTheme() {
      if (!this.themeSheetVisible) return
      const buttons = this.$refs.themeButtons || []
      const button = buttons.find(item => item.getAttribute('aria-pressed') === 'true') || buttons[0]
      if (button?.isConnected) button.focus({ preventScroll: true })
    },
    selectLanguage(key) {
      if (!this.languageSheetVisible || this.isLoading || !this.languageOptions.some(item => item.key === key)) return
      this.languageSheetVisible = false
      if (key === this.languageValue) return
      this.$emit('update:languageValue', key)
      this.$emit('language-change')
    },
    focusSelectedLanguage() {
      if (!this.languageSheetVisible) return
      const buttons = this.$refs.languageButtons || []
      const button = buttons.find(item => item.getAttribute('aria-pressed') === 'true') || buttons[0]
      if (button?.isConnected) button.focus({ preventScroll: true })
    }
  }
}
</script>

<style scoped lang="scss">
@use '../styles/preferences' as mobile;
@include mobile.content;
</style>

<style lang="scss">
@use '../styles/preferences' as mobile;
@include mobile.language-sheet;
</style>
